// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.

import type {
  Review,
  ReviewQueue,
  ReviewQueueMembership,
} from "../_internal/types.gen";
import {
  deleteAdminReviewQueueMembershipsById,
  deleteAdminReviewQueuesById,
  getAdminReviewQueueMemberships,
  getAdminReviewQueueMembershipsById,
  getAdminReviewQueues,
  getAdminReviewQueuesById,
  getAdminReviewQueuesSummaries,
  getAdminReviews,
  getAdminReviewsById,
  patchAdminReviewQueuesById,
  patchAdminReviewsByIdApprove,
  patchAdminReviewsByIdAssign,
  patchAdminReviewsByIdClaim,
  patchAdminReviewsByIdCorrect,
  patchAdminReviewsByIdEscalate,
  patchAdminReviewsByIdReject,
  patchAdminReviewsByIdUnclaim,
  postAdminReviewQueueMemberships,
  postAdminReviewQueues,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { buildPageQuery } from "../namespace-types";
import { RequestBuilder } from "../request-builder";

export type AdminReviewPriority = "low" | "normal" | "high" | "urgent";
export type AdminReviewQueueMembershipRole = "member" | "supervisor";

/** Workspace-scoped review queue summary row. */
export type AdminReviewQueueSummary = {
  queue_id: string;
  slug: string;
  name: string;
  pending_count: number;
  sla_overdue_count: number;
};

export type AdminCreateReviewQueueAttributes = {
  workspace_id: string;
  name: string;
  slug: string;
  description?: string | null;
  default_sla_minutes?: number | null;
};

export type AdminUpdateReviewQueueAttributes = {
  name?: string;
  description?: string | null;
  default_sla_minutes?: number | null;
  is_archived?: boolean;
};

export type AdminCreateReviewQueueMembershipAttributes = {
  workspace_id: string;
  queue_id: string;
  user_id: string;
  membership_role?: AdminReviewQueueMembershipRole;
};

export type AdminAssignReviewAttributes = {
  assigned_user_id: string | null;
};

export type AdminApproveReviewAttributes = {
  decision_payload?: Record<string, unknown> | null;
  notes?: string | null;
};

export type AdminRejectReviewAttributes = {
  decision_payload?: Record<string, unknown> | null;
  notes?: string | null;
};

export type AdminCorrectReviewAttributes = {
  correction_payload?: Record<string, unknown> | null;
  notes?: string | null;
};

export type AdminEscalateReviewAttributes = {
  queue_id?: string;
  assigned_user_id?: string | null;
  priority?: AdminReviewPriority;
};

function buildCreateQueueAttributes(
  attributes: AdminCreateReviewQueueAttributes,
) {
  const { workspace_id, name, slug, description, default_sla_minutes } =
    attributes;

  return { workspace_id, name, slug, description, default_sla_minutes };
}

function buildUpdateQueueAttributes(
  attributes: AdminUpdateReviewQueueAttributes,
) {
  const { name, description, default_sla_minutes, is_archived } = attributes;

  return { name, description, default_sla_minutes, is_archived };
}

function buildCreateMembershipAttributes(
  attributes: AdminCreateReviewQueueMembershipAttributes,
) {
  const { workspace_id, queue_id, user_id, membership_role } = attributes;

  return { workspace_id, queue_id, user_id, membership_role };
}

/**
 * Reviews namespace — admin access to human-review queues, memberships, and lifecycle actions.
 *
 * Admin callers can inspect review queues, grant queue membership, and drive
 * review lifecycle transitions through the JSON:API resources.
 */
export function createReviewsNamespace(rb: RequestBuilder) {
  return {
    /** Review records and lifecycle transitions. */
    reviews: {
      /**
       * List Reviews visible to the admin caller.
       *
       * @param options - Optional pagination and request settings.
       * @returns A promise resolving to Review records.
       */
      list: async (
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<Review[]> => {
        return rb.execute<Review[]>(
          getAdminReviews,
          { ...buildPageQuery(options?.page, options?.pageSize) },
          options,
        );
      },

      /**
       * Fetch a Review by id.
       *
       * @param id - Review id.
       * @param options - Optional request settings.
       * @returns A promise resolving to the Review.
       */
      get: async (id: string, options?: RequestOptions): Promise<Review> => {
        return rb.execute<Review>(
          getAdminReviewsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Assign a Review to a user or clear the assignee.
       *
       * @param id - Review id.
       * @param attributes - Assignment attributes accepted by the Review action.
       * @param options - Optional request settings.
       * @returns A promise resolving to the assigned Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.assign(reviewId, { assigned_user_id: userId });
       * ```
       */
      assign: async (
        id: string,
        attributes: AdminAssignReviewAttributes,
        options?: RequestOptions,
      ): Promise<Review> => {
        const { assigned_user_id } = attributes;

        return rb.execute<Review>(
          patchAdminReviewsByIdAssign,
          {
            path: { id },
            body: {
              data: { type: "review", id, attributes: { assigned_user_id } },
            },
          },
          options,
        );
      },

      /**
       * Claim an open or assigned Review for the current reviewer.
       *
       * @param id - Review id.
       * @param options - Optional request settings.
       * @returns A promise resolving to the claimed Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.claim(reviewId);
       * ```
       */
      claim: async (id: string, options?: RequestOptions): Promise<Review> => {
        return rb.execute<Review>(
          patchAdminReviewsByIdClaim,
          { path: { id }, body: { data: { type: "review", id } } },
          options,
        );
      },

      /**
       * Release a claimed Review back to its queue or assignee.
       *
       * @param id - Review id.
       * @param options - Optional request settings.
       * @returns A promise resolving to the unclaimed Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.unclaim(reviewId);
       * ```
       */
      unclaim: async (
        id: string,
        options?: RequestOptions,
      ): Promise<Review> => {
        return rb.execute<Review>(
          patchAdminReviewsByIdUnclaim,
          { path: { id }, body: { data: { type: "review", id } } },
          options,
        );
      },

      /**
       * Approve a Review and attach an optional decision payload.
       *
       * @param id - Review id.
       * @param attributes - Approval decision attributes.
       * @param options - Optional request settings.
       * @returns A promise resolving to the approved Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.approve(reviewId, { notes: "Verified" });
       * ```
       */
      approve: async (
        id: string,
        attributes: AdminApproveReviewAttributes = {},
        options?: RequestOptions,
      ): Promise<Review> => {
        const { decision_payload, notes } = attributes;

        return rb.execute<Review>(
          patchAdminReviewsByIdApprove,
          {
            path: { id },
            body: {
              data: {
                type: "review",
                id,
                attributes: { decision_payload, notes },
              },
            },
          },
          options,
        );
      },

      /**
       * Reject a Review and attach an optional decision payload.
       *
       * @param id - Review id.
       * @param attributes - Rejection decision attributes.
       * @param options - Optional request settings.
       * @returns A promise resolving to the rejected Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.reject(reviewId, { notes: "Missing consent" });
       * ```
       */
      reject: async (
        id: string,
        attributes: AdminRejectReviewAttributes = {},
        options?: RequestOptions,
      ): Promise<Review> => {
        const { decision_payload, notes } = attributes;

        return rb.execute<Review>(
          patchAdminReviewsByIdReject,
          {
            path: { id },
            body: {
              data: {
                type: "review",
                id,
                attributes: { decision_payload, notes },
              },
            },
          },
          options,
        );
      },

      /**
       * Correct a Review with field-level correction data.
       *
       * @param id - Review id.
       * @param attributes - Correction payload and optional notes.
       * @param options - Optional request settings.
       * @returns A promise resolving to the corrected Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.correct(reviewId, { correction_payload: { total: 42 } });
       * ```
       */
      correct: async (
        id: string,
        attributes: AdminCorrectReviewAttributes = {},
        options?: RequestOptions,
      ): Promise<Review> => {
        const { correction_payload, notes } = attributes;

        return rb.execute<Review>(
          patchAdminReviewsByIdCorrect,
          {
            path: { id },
            body: {
              data: {
                type: "review",
                id,
                attributes: { correction_payload, notes },
              },
            },
          },
          options,
        );
      },

      /**
       * Escalate a Review to another queue, assignee, or priority.
       *
       * @param id - Review id.
       * @param attributes - Escalation attributes accepted by the Review action.
       * @param options - Optional request settings.
       * @returns A promise resolving to the escalated Review.
       * @example
       * ```typescript
       * await admin.reviews.reviews.escalate(reviewId, { queue_id: supervisorQueueId });
       * ```
       */
      escalate: async (
        id: string,
        attributes: AdminEscalateReviewAttributes,
        options?: RequestOptions,
      ): Promise<Review> => {
        const { queue_id, assigned_user_id, priority } = attributes;

        return rb.execute<Review>(
          patchAdminReviewsByIdEscalate,
          {
            path: { id },
            body: {
              data: {
                type: "review",
                id,
                attributes: { queue_id, assigned_user_id, priority },
              },
            },
          },
          options,
        );
      },
    },

    /** Review queues used to route human work. */
    queues: {
      /**
       * List ReviewQueues visible to the admin caller.
       *
       * @param options - Optional pagination and request settings.
       * @returns A promise resolving to ReviewQueue records.
       */
      list: async (
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<ReviewQueue[]> => {
        return rb.execute<ReviewQueue[]>(
          getAdminReviewQueues,
          { ...buildPageQuery(options?.page, options?.pageSize) },
          options,
        );
      },

      /**
       * Fetch a ReviewQueue by id.
       *
       * @param id - ReviewQueue id.
       * @param options - Optional request settings.
       * @returns A promise resolving to the ReviewQueue.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ReviewQueue> => {
        return rb.execute<ReviewQueue>(
          getAdminReviewQueuesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Return review queue summary rows for a workspace.
       *
       * @param workspaceId - Workspace UUID to scope the admin request.
       * @param options - Optional request settings.
       * @returns A promise resolving to non-archived queue summary rows.
       */
      summaries: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<AdminReviewQueueSummary[]> => {
        return rb.execute<AdminReviewQueueSummary[]>(
          getAdminReviewQueuesSummaries,
          { query: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Create a ReviewQueue.
       *
       * @param attributes - Accepted ReviewQueue create attributes.
       * @param options - Optional request settings.
       * @returns A promise resolving to the created ReviewQueue.
       * @example
       * ```typescript
       * await admin.reviews.queues.create({ workspace_id, name, slug });
       * ```
       */
      create: async (
        attributes: AdminCreateReviewQueueAttributes,
        options?: RequestOptions,
      ): Promise<ReviewQueue> => {
        const bodyAttributes = buildCreateQueueAttributes(attributes);

        return rb.execute<ReviewQueue>(
          postAdminReviewQueues,
          {
            body: {
              data: { type: "review-queue", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Update mutable ReviewQueue fields.
       *
       * @param id - ReviewQueue id.
       * @param attributes - Accepted ReviewQueue update attributes.
       * @param options - Optional request settings.
       * @returns A promise resolving to the updated ReviewQueue.
       * @example
       * ```typescript
       * await admin.reviews.queues.update(queueId, { is_archived: true });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdateReviewQueueAttributes,
        options?: RequestOptions,
      ): Promise<ReviewQueue> => {
        const bodyAttributes = buildUpdateQueueAttributes(attributes);

        return rb.execute<ReviewQueue>(
          patchAdminReviewQueuesById,
          {
            path: { id },
            body: {
              data: { type: "review-queue", id, attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Delete a ReviewQueue.
       *
       * @param id - ReviewQueue id.
       * @param options - Optional request settings.
       * @returns A promise resolving to true when the delete succeeds.
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminReviewQueuesById,
          { path: { id } },
          options,
        );
      },
    },

    /** Review queue memberships for reviewer and supervisor access. */
    memberships: {
      /**
       * List ReviewQueueMembership records visible to the admin caller.
       *
       * @param options - Optional pagination and request settings.
       * @returns A promise resolving to ReviewQueueMembership records.
       */
      list: async (
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<ReviewQueueMembership[]> => {
        return rb.execute<ReviewQueueMembership[]>(
          getAdminReviewQueueMemberships,
          { ...buildPageQuery(options?.page, options?.pageSize) },
          options,
        );
      },

      /**
       * Fetch a ReviewQueueMembership by id.
       *
       * @param id - ReviewQueueMembership id.
       * @param options - Optional request settings.
       * @returns A promise resolving to the ReviewQueueMembership.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ReviewQueueMembership> => {
        return rb.execute<ReviewQueueMembership>(
          getAdminReviewQueueMembershipsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Grant a user membership on a ReviewQueue.
       *
       * @param attributes - Accepted ReviewQueueMembership create attributes.
       * @param options - Optional request settings.
       * @returns A promise resolving to the created ReviewQueueMembership.
       * @example
       * ```typescript
       * await admin.reviews.memberships.create({ workspace_id, queue_id, user_id });
       * ```
       */
      create: async (
        attributes: AdminCreateReviewQueueMembershipAttributes,
        options?: RequestOptions,
      ): Promise<ReviewQueueMembership> => {
        const bodyAttributes = buildCreateMembershipAttributes(attributes);

        return rb.execute<ReviewQueueMembership>(
          postAdminReviewQueueMemberships,
          {
            body: {
              data: {
                type: "review-queue-membership",
                attributes: bodyAttributes,
              },
            },
          },
          options,
        );
      },

      /**
       * Revoke a ReviewQueueMembership.
       *
       * @param id - ReviewQueueMembership id.
       * @param options - Optional request settings.
       * @returns A promise resolving to true when the delete succeeds.
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminReviewQueueMembershipsById,
          { path: { id } },
          options,
        );
      },
    },
  };
}
